import { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Layout from '@/components/Layout';
import { AuthContext } from '@/App';
import axios from 'axios';
import { ChevronLeft, ChevronRight, CheckCircle, Target } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SmartGoalWizard() {
  const { courseId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [syllabusItem, setSyllabusItem] = useState(null);
  const [course, setCourse] = useState(null);
  
  // Form responses
  const [responses, setResponses] = useState({
    specific: {},
    measurable: {},
    achievable: {},
    relevant: {},
    timebound: {},
    learning_style: {},
    team: {},
    risk: {},
    calendar: {}
  });

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    
    // Get item from location state
    const item = location.state?.item;
    const courseData = location.state?.course;
    
    if (!item || !courseData) {
      toast.error('Missing item information');
      navigate(`/study-buddy/course/${courseId}`);
      return;
    }
    
    setSyllabusItem(item);
    setCourse(courseData);
  }, [user, courseId, location]);

  const totalSteps = 10;

  const updateResponse = (category, field, value) => {
    setResponses(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value
      }
    }));
  };

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    
    try {
      const response = await axios.post(
        `${API}/study-buddy/smart-goals`,
        {
          course_id: courseId,
          syllabus_item_id: syllabusItem.item_id,
          responses: responses
        }
      );
      
      toast.success('S.M.A.R.T. Goal created successfully!');
      navigate(`/study-buddy/smart-goal/${response.data.id}`);
    } catch (error) {
      console.error('Error creating goal:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to create goal';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Failed to create goal');
    } finally {
      setLoading(false);
    }
  };

  if (!syllabusItem || !course) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E8934D]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate(`/study-buddy/course/${courseId}`)}
              className="text-[#2C1810] hover:text-[#E85D4D] font-semibold mb-4 flex items-center gap-2"
            >
              ← Back to Course
            </button>
            
            <div className="flex items-center gap-3 mb-4">
              <Target className="w-8 h-8 text-[#E8934D]" />
              <h1 className="text-4xl font-bold retro-title text-[#2C1810]">
                Create S.M.A.R.T. Goal
              </h1>
            </div>
            
            <div className="bg-[#E8934D]/10 border-2 border-[#E8934D] rounded-lg p-4">
              <p className="text-sm text-gray-700 mb-1">For Assignment:</p>
              <p className="text-xl font-bold text-[#2C1810]">{syllabusItem.title}</p>
              <p className="text-sm text-gray-600 mt-1">
                {course.course_name} • Due: {syllabusItem.due_date}
              </p>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-bold text-gray-700">
                Step {currentStep} of {totalSteps}
              </span>
              <span className="text-sm text-gray-600">
                {Math.round((currentStep / totalSteps) * 100)}% Complete
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-[#E8934D] h-3 rounded-full transition-all duration-300"
                style={{ width: `${(currentStep / totalSteps) * 100}%` }}
              />
            </div>
          </div>

          {/* Question Sections */}
          <div className="bg-white border-4 border-[#E8934D] rounded-lg shadow-lg p-8 mb-6">
            {currentStep === 1 && <Step1Specific responses={responses.specific} updateResponse={updateResponse} item={syllabusItem} />}
            {currentStep === 2 && <Step2Measurable responses={responses.measurable} updateResponse={updateResponse} />}
            {currentStep === 3 && <Step3Achievable responses={responses.achievable} updateResponse={updateResponse} />}
            {currentStep === 4 && <Step4Relevant responses={responses.relevant} updateResponse={updateResponse} />}
            {currentStep === 5 && <Step5Timebound responses={responses.timebound} updateResponse={updateResponse} item={syllabusItem} />}
            {currentStep === 6 && <Step6LearningStyle responses={responses.learning_style} updateResponse={updateResponse} />}
            {currentStep === 7 && <Step7Team responses={responses.team} updateResponse={updateResponse} />}
            {currentStep === 8 && <Step8Risk responses={responses.risk} updateResponse={updateResponse} />}
            {currentStep === 9 && <Step9Calendar responses={responses.calendar} updateResponse={updateResponse} />}
            {currentStep === 10 && <Step10Summary responses={responses} item={syllabusItem} course={course} />}
          </div>

          {/* Navigation Buttons */}
          <div className="flex gap-4">
            {currentStep > 1 && (
              <button
                onClick={handleBack}
                className="flex-1 px-6 py-3 bg-gray-200 text-gray-800 font-bold rounded-lg hover:bg-gray-300 transition-colors flex items-center justify-center gap-2"
              >
                <ChevronLeft className="w-5 h-5" />
                Back
              </button>
            )}
            
            {currentStep < totalSteps ? (
              <button
                onClick={handleNext}
                className="flex-1 px-6 py-3 bg-[#E8934D] text-white font-bold rounded-lg hover:bg-[#E85D4D] transition-colors flex items-center justify-center gap-2"
              >
                Next
                <ChevronRight className="w-5 h-5" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="flex-1 px-6 py-3 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Generating Schedule...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    Generate My Study Plan
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

// Step Components
function Step1Specific({ responses, updateResponse, item }) {
  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-[#2C1810]">Step 1: Specific - Define What, Why, and Scope</h2>
      
      <div>
        <label className="block font-bold text-gray-700 mb-2">
          What exactly is the project deliverable?
        </label>
        <select
          value={responses.deliverable_type || ''}
          onChange={(e) => updateResponse('specific', 'deliverable_type', e.target.value)}
          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#E8934D] focus:outline-none text-gray-900 bg-white"
        >
          <option value="">Select type...</option>
          <option value="paper">Paper</option>
          <option value="presentation">Presentation</option>
          <option value="prototype">Prototype</option>
          <option value="exam">Exam</option>
          <option value="lab">Lab Report</option>
          <option value="other">Other</option>
        </select>
      </div>

      <div>
        <label className="block font-bold text-gray-700 mb-2">
          What specific topic, question, or problem will you address?
        </label>
        <p className="text-sm text-gray-600 mb-2 italic">
          Example: "I will analyze the therapeutic effects of acupuncture point ST36 on digestive disorders"
        </p>
        <textarea
          value={responses.topic || ''}
          onChange={(e) => updateResponse('specific', 'topic', e.target.value)}
          rows={4}
          placeholder="Describe the specific focus of your work..."
          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#E8934D] focus:outline-none text-gray-900 bg-white"
        />
      </div>

      <div>
        <label className="block font-bold text-gray-700 mb-2">
          What will success look like for this project? (One sentence)
        </label>
        <p className="text-sm text-gray-600 mb-2 italic">
          Example: "A comprehensive research paper scoring 90% or higher with clear evidence and proper citations"
        </p>
        <input
          type="text"
          value={responses.success_statement || ''}
          onChange={(e) => updateResponse('specific', 'success_statement', e.target.value)}
          placeholder="E.g., A well-researched 10-page paper that earns an A grade"
          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#E8934D] focus:outline-none text-gray-900 bg-white"
        />
      </div>
    </div>
  );
}

function Step2Measurable({ responses, updateResponse }) {
  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-[#2C1810]">Step 2: Measurable - Define Progress Tracking</h2>
      
      <div>
        <label className="block font-bold text-gray-700 mb-2">
          How many hours per week can you commit to this project?
        </label>
        <input
          type="number"
          min="1"
          max="40"
          value={responses.hours_per_week || ''}
          onChange={(e) => updateResponse('measurable', 'hours_per_week', e.target.value)}
          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#E8934D] focus:outline-none text-gray-900 bg-white"
        />
      </div>

      <div>
        <label className="block font-bold text-gray-700 mb-2">
          What measurable outputs will indicate progress?
        </label>
        <div className="space-y-2">
          {['Pages written', 'Slides completed', 'Practice tests taken', 'Chapters read', 'Hours studied'].map(option => (
            <label key={option} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={responses.progress_indicators?.includes(option) || false}
                onChange={(e) => {
                  const current = responses.progress_indicators || [];
                  const updated = e.target.checked
                    ? [...current, option]
                    : current.filter(item => item !== option);
                  updateResponse('measurable', 'progress_indicators', updated);
                }}
                className="w-4 h-4"
              />
              <span className="text-gray-700">{option}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

function Step3Achievable({ responses, updateResponse }) {
  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-[#2C1810]">Step 3: Achievable - Assess Feasibility & Resources</h2>
      
      <div>
        <label className="block font-bold text-gray-700 mb-2">
          What skills do you already have that are relevant?
        </label>
        <p className="text-sm text-gray-600 mb-2 italic">
          Example: "Strong research skills, experience with academic writing, familiar with TCM theory, good at organizing information"
        </p>
        <textarea
          value={responses.existing_skills || ''}
          onChange={(e) => updateResponse('achievable', 'existing_skills', e.target.value)}
          rows={3}
          placeholder="List your relevant skills and experience..."
          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#E8934D] focus:outline-none text-gray-900 bg-white"
        />
      </div>

      <div>
        <label className="block font-bold text-gray-700 mb-2">
          What resources are available to you?
        </label>
        <div className="space-y-2">
          {['Library access', 'Lab equipment', 'Software tools', 'Office hours', 'Study groups', 'Online resources'].map(option => (
            <label key={option} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={responses.resources?.includes(option) || false}
                onChange={(e) => {
                  const current = responses.resources || [];
                  const updated = e.target.checked
                    ? [...current, option]
                    : current.filter(item => item !== option);
                  updateResponse('achievable', 'resources', updated);
                }}
                className="w-4 h-4"
              />
              <span className="text-gray-700">{option}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

function Step4Relevant({ responses, updateResponse }) {
  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-[#2C1810]">Step 4: Relevant - Fit to Priorities & Goals</h2>
      
      <div>
        <label className="block font-bold text-gray-700 mb-2">
          How does this project align with your academic or career goals?
        </label>
        <p className="text-sm text-gray-600 mb-2 italic">
          Example: "This research will strengthen my understanding of acupuncture point theory, which is essential for my future practice as a licensed acupuncturist"
        </p>
        <textarea
          value={responses.alignment || ''}
          onChange={(e) => updateResponse('relevant', 'alignment', e.target.value)}
          rows={4}
          placeholder="Explain how this work contributes to your larger goals..."
          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#E8934D] focus:outline-none text-gray-900 bg-white"
        />
      </div>

      <div>
        <label className="block font-bold text-gray-700 mb-2">
          Why is this project important to you personally or professionally?
        </label>
        <p className="text-sm text-gray-600 mb-2 italic">
          Example: "I'm passionate about helping patients with digestive issues, and understanding this material deeply will make me a better practitioner"
        </p>
        <textarea
          value={responses.importance || ''}
          onChange={(e) => updateResponse('relevant', 'importance', e.target.value)}
          rows={3}
          placeholder="Your personal motivation..."
          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#E8934D] focus:outline-none text-gray-900 bg-white"
        />
      </div>
    </div>
  );
}

function Step5Timebound({ responses, updateResponse, item }) {
  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-[#2C1810]">Step 5: Time-bound - Set Deadlines and Schedule</h2>
      
      <div className="p-4 bg-[#E85D4D]/10 border-2 border-[#E85D4D] rounded-lg">
        <p className="text-sm text-gray-700">Final Due Date:</p>
        <p className="text-xl font-bold text-[#E85D4D]">{item.due_date}</p>
      </div>

      <div>
        <label className="block font-bold text-gray-700 mb-2">
          When is your personal deadline to have a "first complete draft" ready?
        </label>
        <input
          type="date"
          value={responses.draft_deadline || ''}
          onChange={(e) => updateResponse('timebound', 'draft_deadline', e.target.value)}
          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#E8934D] focus:outline-none text-gray-900 bg-white"
        />
      </div>

      <div>
        <label className="block font-bold text-gray-700 mb-2">
          How many revision cycles will you plan?
        </label>
        <input
          type="number"
          min="1"
          max="5"
          value={responses.revision_cycles || ''}
          onChange={(e) => updateResponse('timebound', 'revision_cycles', e.target.value)}
          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#E8934D] focus:outline-none text-gray-900 bg-white"
        />
      </div>
    </div>
  );
}

function Step6LearningStyle({ responses, updateResponse }) {
  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-[#2C1810]">Step 6: Learning Style & Habits</h2>
      
      <div>
        <label className="block font-bold text-gray-700 mb-2">
          When during the day are you most focused and productive?
        </label>
        <div className="space-y-2">
          {['Early morning (5-8am)', 'Morning (8-12pm)', 'Afternoon (12-5pm)', 'Evening (5-9pm)', 'Night (9pm-midnight)'].map(option => (
            <label key={option} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={responses.productive_times?.includes(option) || false}
                onChange={(e) => {
                  const current = responses.productive_times || [];
                  const updated = e.target.checked
                    ? [...current, option]
                    : current.filter(item => item !== option);
                  updateResponse('learning_style', 'productive_times', updated);
                }}
                className="w-4 h-4"
              />
              <span className="text-gray-700">{option}</span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <label className="block font-bold text-gray-700 mb-2">
          Do you work better in long blocks or short frequent sessions?
        </label>
        <select
          value={responses.session_type || ''}
          onChange={(e) => updateResponse('learning_style', 'session_type', e.target.value)}
          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#E8934D] focus:outline-none text-gray-900 bg-white"
        >
          <option value="">Select...</option>
          <option value="long">Long blocks (2+ hours)</option>
          <option value="medium">Medium blocks (1-2 hours)</option>
          <option value="short">Short sessions (30-60 min)</option>
          <option value="pomodoro">Pomodoro (25 min sprints)</option>
        </select>
      </div>
    </div>
  );
}

function Step7Team({ responses, updateResponse }) {
  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-[#2C1810]">Step 7: Team & Collaboration</h2>
      
      <div>
        <label className="block font-bold text-gray-700 mb-2">
          Is this a group project?
        </label>
        <div className="flex gap-4">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={responses.is_group_project === 'yes'}
              onChange={() => updateResponse('team', 'is_group_project', 'yes')}
              className="w-4 h-4"
            />
            <span className="text-gray-700">Yes</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={responses.is_group_project === 'no'}
              onChange={() => updateResponse('team', 'is_group_project', 'no')}
              className="w-4 h-4"
            />
            <span className="text-gray-700">No</span>
          </label>
        </div>
      </div>

      {responses.is_group_project === 'yes' && (
        <div>
          <label className="block font-bold text-gray-700 mb-2">
            How will the team communicate?
          </label>
          <p className="text-sm text-gray-600 mb-2 italic">
            Example: "Weekly Zoom meetings on Wednesdays, daily updates in our Slack channel"
          </p>
          <input
            type="text"
            value={responses.team_communication || ''}
            onChange={(e) => updateResponse('team', 'team_communication', e.target.value)}
            placeholder="E.g., Zoom, Slack, Discord..."
            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#E8934D] focus:outline-none text-gray-900 bg-white"
          />
        </div>
      )}
    </div>
  );
}

function Step8Risk({ responses, updateResponse }) {
  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-[#2C1810]">Step 8: Risk & Contingency Planning</h2>
      
      <div>
        <label className="block font-bold text-gray-700 mb-2">
          What are the main risks that could prevent on-time completion?
        </label>
        <p className="text-sm text-gray-600 mb-2 italic">
          Example: "Heavy workload from other classes, difficulty finding research sources, potential illness, procrastination"
        </p>
        <textarea
          value={responses.main_risks || ''}
          onChange={(e) => updateResponse('risk', 'main_risks', e.target.value)}
          rows={4}
          placeholder="List potential obstacles or challenges..."
          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#E8934D] focus:outline-none text-gray-900 bg-white"
        />
      </div>

      <div>
        <label className="block font-bold text-gray-700 mb-2">
          What's your backup plan for technical failures or unavailable resources?
        </label>
        <p className="text-sm text-gray-600 mb-2 italic">
          Example: "Keep backup copies in Google Drive and USB, have alternate library as backup, identified 2 classmates I can ask for help"
        </p>
        <textarea
          value={responses.backup_plan || ''}
          onChange={(e) => updateResponse('risk', 'backup_plan', e.target.value)}
          rows={3}
          placeholder="Your contingency plans..."
          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#E8934D] focus:outline-none text-gray-900 bg-white"
        />
      </div>
    </div>
  );
}

function Step9Calendar({ responses, updateResponse }) {
  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-[#2C1810]">Step 9: Calendar Integration & Automation</h2>
      
      <div>
        <label className="block font-bold text-gray-700 mb-2">
          How often do you want automated progress check-ins?
        </label>
        <select
          value={responses.checkin_frequency || ''}
          onChange={(e) => updateResponse('calendar', 'checkin_frequency', e.target.value)}
          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#E8934D] focus:outline-none text-gray-900 bg-white"
        >
          <option value="">Select...</option>
          <option value="daily">Daily</option>
          <option value="every_2_days">Every 2 days</option>
          <option value="weekly">Weekly</option>
          <option value="milestone">Milestone-based</option>
          <option value="none">None</option>
        </select>
      </div>

      <div>
        <label className="block font-bold text-gray-700 mb-2">
          What notification lead times do you prefer?
        </label>
        <div className="space-y-2">
          {['24 hours', '72 hours', '1 week', '2 weeks'].map(option => (
            <label key={option} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={responses.notification_times?.includes(option) || false}
                onChange={(e) => {
                  const current = responses.notification_times || [];
                  const updated = e.target.checked
                    ? [...current, option]
                    : current.filter(item => item !== option);
                  updateResponse('calendar', 'notification_times', updated);
                }}
                className="w-4 h-4"
              />
              <span className="text-gray-700">{option} before deadline</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

function Step10Summary({ responses, item, course }) {
  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-[#2C1810] mb-4">Step 10: Review & Confirm</h2>
      
      <div className="bg-green-50 border-2 border-green-500 rounded-lg p-6">
        <h3 className="text-lg font-bold text-green-800 mb-3 flex items-center gap-2">
          <CheckCircle className="w-6 h-6" />
          You're Ready to Generate Your S.M.A.R.T. Goal!
        </h3>
        <p className="text-green-700 mb-4">
          Based on your responses, our AI will create a personalized study schedule tailored to your learning style, available time, and goals.
        </p>
        
        <div className="space-y-2 text-sm">
          <p className="text-gray-700">
            <span className="font-bold">Assignment:</span> {item.title}
          </p>
          <p className="text-gray-700">
            <span className="font-bold">Due Date:</span> {item.due_date}
          </p>
          <p className="text-gray-700">
            <span className="font-bold">Course:</span> {course.course_name}
          </p>
          <p className="text-gray-700">
            <span className="font-bold">Hours/Week:</span> {responses.measurable.hours_per_week || 'Not specified'}
          </p>
          <p className="text-gray-700">
            <span className="font-bold">Study Style:</span> {responses.learning_style.session_type || 'Not specified'}
          </p>
        </div>
      </div>

      <div className="bg-[#E8934D]/10 border-2 border-[#E8934D] rounded-lg p-6">
        <h3 className="text-lg font-bold text-[#2C1810] mb-2">What happens next?</h3>
        <ul className="space-y-2 text-sm text-gray-700">
          <li className="flex items-start gap-2">
            <span className="text-[#E8934D] font-bold">1.</span>
            <span>AI analyzes your responses and creates a personalized schedule</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-[#E8934D] font-bold">2.</span>
            <span>Study sessions are broken into manageable time blocks</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-[#E8934D] font-bold">3.</span>
            <span>Milestones are created with specific deliverables</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-[#E8934D] font-bold">4.</span>
            <span>Calendar events are added to your course schedule</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-[#E8934D] font-bold">5.</span>
            <span>You can track progress and check off completed sessions</span>
          </li>
        </ul>
      </div>

      <p className="text-center text-gray-600 text-sm">
        Click "Generate My Study Plan" below to create your S.M.A.R.T. Goal!
      </p>
    </div>
  );
}
